import { VALOR_STATUS_NAO_CONCLUIDO } from './filtroDinamico';

/**
 * Quais chamados FINALIZADOS a listagem precisa baixar para um filtro.
 *
 * ## O que isto decide, e o que NÃO decide
 *
 * Não decide o que a tela mostra -- `applyFilterConditions` continua
 * decidindo. Decide o que BAIXAR: a listagem recebe os não finalizados em
 * bloco (`scope=active`) e os finalizados sob demanda, de 100 em 100 por
 * `updated_at`. Quem não é baixado não existe para a tela, e some sem erro
 * nenhum.
 *
 * ## O defeito que isto conserta (ponto 33 da Gisele, 20/10/2026)
 *
 * "Corrigir os filtros do histórico: com os status Cancelado e Resolvido
 * incluídos, a listagem ainda não carrega direito."
 *
 * A versão anterior varria as condições de `status_id` e colecionava os
 * valores -- IGNORANDO o operador. Com isso, todo operador NEGATIVO ficava
 * invertido: baixava exatamente os status que o filtro exclui e deixava de
 * fora os que ele quer.
 *
 *   - "Não é um de [Novo, Aberto, Em atendimento]" é como se escreve
 *     "só o histórico". Nenhum dos três é finalizado, então o conjunto
 *     referenciado saía VAZIO -> zero finalizados baixados -> a listagem do
 *     histórico mostrava nada;
 *   - "Diferente de Fechado" (ou os três `not_equals` que dois kanbans de
 *     produção usam para dizer "não finalizado") baixava justamente o
 *     Fechado, que a condição logo descartava.
 *
 * `is_not_null` tinha um terceiro jeito de dar o mesmo prejuízo: não
 * referenciava nada, mas CONTAVA como condição de status -- e a listagem
 * entende "tem condição de status" como "respeite o que ela pede", então
 * caía em lista vazia em vez de cair no padrão de trazer todos.
 *
 * ## Por que a UNIÃO entre condições, e não a interseção
 *
 * As condições do filtro moram em dois grupos (um "e" e um "ou") e esta
 * função não reproduz essa álgebra -- reproduzi-la aqui seria uma segunda
 * implementação do filtro, que divergiria da primeira.
 *
 * A união é o lado seguro: baixar DEMAIS a tela corrige (o recorte final é
 * client-side, e o servidor ainda recebe o `filter_id` e aplica as condições
 * na paginação, então nem a ordem se perde); baixar DE MENOS ela não tem como
 * corrigir, porque o chamado não chegou.
 *
 * @param {object|null} filtro filtro selecionado, com `conditions`
 * @param {Array<number>} finalizados ids dos status finais (Fechado/Cancelado/Resolvido)
 * @param {string|number|null} statusDaBuscaAvancada status escolhido na Busca Avançada
 * @returns {Array<number>} subconjunto de `finalizados`, na ordem original
 */
export const finalizadosReferenciados = (filtro, finalizados, statusDaBuscaAvancada) => {
    const todos = (Array.isArray(finalizados) ? finalizados : []).map((id) => Number(id));
    if (todos.length === 0) return [];

    const referenciados = new Set();
    const todosReferenciados = () => todos.forEach((id) => referenciados.add(id));

    (filtro?.conditions || []).forEach((c) => {
        const campo = c.field_name || c.field;
        if (campo !== 'status_id') return;

        const operador = c.operator || c.operador;

        // Sem valor nenhum: a condição só diz se o campo está preenchido.
        // "Está preenchido" inclui todo finalizado; "está vazio" nenhum.
        if (operador === 'is_not_null') { todosReferenciados(); return; }
        if (operador === 'is_null') return;

        const valores = valoresDaCondicao(c.value);

        /*
         * O marcador "Não concluído" não é um id -- `Number()` nele dá `NaN`.
         * Ele NEGA o conjunto todo, então inverte junto com o operador:
         * "é igual a Não concluído" exclui todo finalizado, "diferente de"
         * pede exatamente eles.
         */
        const temMarcador = valores.some((v) => v === VALOR_STATUS_NAO_CONCLUIDO);
        const ids = valores
            .filter((v) => v !== VALOR_STATUS_NAO_CONCLUIDO)
            .map((v) => Number(v))
            .filter((v) => !Number.isNaN(v));

        if (ehNegativo(operador)) {
            // O filtro pede o COMPLEMENTO: todo finalizado que a condição não
            // nomeia. Era aqui que a lista do histórico se perdia.
            if (temMarcador) todosReferenciados();
            todos.filter((id) => !ids.includes(id)).forEach((id) => referenciados.add(id));
            return;
        }

        // Positivo: só o que a condição nomeia. O marcador não nomeia
        // finalizado nenhum (é justamente o que ele exclui).
        ids.forEach((id) => referenciados.add(id));
    });

    if (statusDaBuscaAvancada !== VALOR_STATUS_NAO_CONCLUIDO
        && statusDaBuscaAvancada !== ''
        && statusDaBuscaAvancada != null) {
        referenciados.add(Number(statusDaBuscaAvancada));
    }

    return todos.filter((id) => referenciados.has(id));
};

/**
 * O filtro diz algo sobre `status_id`?
 *
 * Quando não diz, a listagem traz TODOS os finalizados -- "mostrar tudo".
 * Quando diz, ela respeita o que `finalizadosReferenciados` devolveu, até a
 * lista vazia. Por isso as duas funções têm de concordar sobre o que é uma
 * condição de status: enquanto `is_not_null` contava aqui e não contava lá, o
 * filtro "status está preenchido" não trazia finalizado nenhum.
 */
export const temCondicaoDeStatus = (filtro, statusDaBuscaAvancada) => {
    const naCondicao = (filtro?.conditions || [])
        .some((c) => (c.field_name || c.field) === 'status_id');

    return naCondicao || (statusDaBuscaAvancada !== '' && statusDaBuscaAvancada != null);
};

/**
 * Os operadores que pedem o COMPLEMENTO do que nomeiam.
 *
 * Lista explícita, e não "tudo que começa com not_": `not_contains`,
 * `not_today` e os outros `not_*` de data nunca aparecem em `status_id`, e um
 * teste de prefixo passaria a abraçá-los em silêncio se um dia aparecerem.
 */
const NEGATIVOS = ['not_equals', 'not_in'];

const ehNegativo = (operador) => NEGATIVOS.includes(operador);

/**
 * Os valores de uma condição, que chegam de três formas.
 *
 * O `value` é uma coluna de texto no banco: um id solto vem como `"5"`, uma
 * multi-seleção vem como JSON (`"[5,6]"`), e filtro antigo gravado à mão vem
 * como `"5,6"`. Ler só uma das três faz a condição valer zero.
 */
const valoresDaCondicao = (bruto) => {
    let valores = bruto;

    if (typeof valores === 'string') {
        try {
            valores = JSON.parse(valores);
        } catch {
            valores = String(valores).split(',').map((s) => s.trim());
        }
    }

    return Array.isArray(valores) ? valores : [valores];
};
